import {
  KnowledgeOwnerType,
  KnowledgePageContent,
  KnowledgePageMeta,
  KnowledgePageVersionDiff,
  KnowledgeSpace,
  KnowledgeVersionInfo,
} from '@this+that/models';

import type { KnowledgeContentRejectedIssue } from '../../hooks/Brain/Knowledge/Page/errors/knowledgePageErrors';
import type { KnowledgePageContentDetail } from '../../services/knowledge.service';

export type BrainSpace = KnowledgeSpace & { brainManaged?: boolean | null };

export type BrainSpaceGroupKey = 'SYSTEM' | KnowledgeOwnerType;

export interface PageHistoryEntry {
  versions: KnowledgeVersionInfo[];
  cursor: string | null;
  hasMore: boolean;
  selectedVersion: number | null;
  currentVersion: number | null;
  loaded: boolean;
  diffByVersion: Record<number, KnowledgePageVersionDiff>;
  contentByVersion: Record<number, KnowledgePageContentDetail>;
}

export enum BrainPageSaveStatus {
  IDLE = 'IDLE',
  SAVING = 'SAVING',
  SAVED = 'SAVED',
  ERROR = 'ERROR',
}

export interface BrainPageConflict {
  serverVersion: number;
  serverContent: string;
  detectedAt: number;
}

export interface BrainPageDraft {
  content: string;
  baseVersion: number;
  saveStatus: BrainPageSaveStatus;
  lastSavedAt: number | null;
  error: string | null;
  conflict: BrainPageConflict | null;
  contentRejection: KnowledgeContentRejectedIssue[] | null;
}

export interface BrainPageEntry {
  server: KnowledgePageContent | null;
  isLoading: boolean;
  error: string | null;
  draft: BrainPageDraft | null;
  resetNonce: number;
}

export interface BrainMobilePageHeaderCreationMeta {
  creatorName: string;
  createdDateLabel: string;
  createdInitials: string;
  createdByCaption: string;
  creationDetailsAriaLabel: string;
}

export interface BrainMobilePageHeader {
  title: string;
  subtitle?: string | null;
  creationMeta?: BrainMobilePageHeaderCreationMeta;
  isEditing?: boolean;
  isSaving?: boolean;
  onSave?: () => void;
  onDiscard?: () => void;
  isFavorited?: boolean;
  onToggleFavorite?: () => void;
  onOpenHistory?: () => void;
}

export interface PendingPageLocation {
  pageId: string;
  spaceId: string;
}

export interface RecentMoveTarget {
  targetSpaceId: string;
  targetParentPageId?: string;
  label: string;
}

export interface BrainState {
  spaces: BrainSpace[] | null;
  isLoadingSpaces: boolean;
  isLoadingUserSpaces: boolean;
  isLoadingTeamSpaces: boolean;
  hasLoadedUserSpaces: boolean;
  hasLoadedTeamSpaces: boolean;
  loadError: string | null;
  expandedPageIds: Set<string>;
  expandedSpaceIds: Set<string>;
  expandedGroupTypes: Set<BrainSpaceGroupKey>;
  selectedPageId: string | null;
  pagesById: Record<string, BrainPageEntry>;
  mobileBrainPageHeader: BrainMobilePageHeader | null;
  _hasHydrated: boolean;
  activeMoveCount: number;
  favoritePageIds: string[];
  favoritePages: KnowledgePageMeta[];
  isLoadingFavorites: boolean;
  loadedSpaceIds: Set<string>;
  loadingSpaceIds: Set<string>;
  spacePageErrors: Record<string, string>;
  pendingPageLocation: PendingPageLocation | null;
  // spaceId/pageId currently being resolved by a cross-team deep-link restore;
  // gates the loading skeleton so the resolve window doesn't flash not-found/home.
  pendingDeepLink: string | null;
  // spaceId open in the URL; kept across a paginated sidebar refresh so a
  // deep-linked space beyond the first page isn't evicted (and re-resolved).
  viewedSpaceId: string | null;
  removedPageIds: Set<string>;
  removedSpaceIds: Set<string>;
  recentPages: KnowledgePageMeta[];
  isLoadingRecentPages: boolean;
  recentPagesStale: boolean;
  pageHistoryById: Record<string, PageHistoryEntry>;
  recentMoveTargets: RecentMoveTarget[];
  userSpacesCursor: string | null;
  teamSpacesCursor: string | null;
  userSpacesEpoch: number;
  teamSpacesEpoch: number;
  userSpacesLoadingMoreEpoch: number | null;
  teamSpacesLoadingMoreEpoch: number | null;
}

export interface MoveSubtreeBetweenSpacesInput {
  sourceSpaceId: string;
  targetSpaceId: string;
  subtreePages: KnowledgePageMeta[];
  rootPageId: string;
  newRootParentPageId: string | undefined;
}

export interface BrainActions {
  setUserSpaces: (spaces: BrainSpace[], cursor: string | null) => void;
  setTeamSpaces: (
    spaces: BrainSpace[],
    cursor: string | null,
    teamId: string,
  ) => void;
  setLoadingUserSpaces: (loading: boolean) => void;
  setLoadingTeamSpaces: (loading: boolean) => void;
  setLoadError: (error: string | null) => void;
  addSpace: (space: BrainSpace) => void;
  removeSpace: (spaceId: string, opts?: { tombstone?: boolean }) => void;
  updateSpace: (space: BrainSpace) => void;
  replaceSpace: (oldSpaceId: string, space: BrainSpace) => void;
  addPageToSpace: (spaceId: string, page: KnowledgePageMeta) => void;
  removePageFromSpace: (spaceId: string, pageId: string) => void;
  replacePageInSpace: (
    spaceId: string,
    oldPageId: string,
    page: KnowledgePageMeta,
  ) => void;
  updatePageInSpace: (spaceId: string, page: KnowledgePageMeta) => void;
  toggleExpandPage: (pageId: string) => void;
  expandPage: (pageId: string) => void;
  collapsePage: (pageId: string) => void;
  toggleExpandSpace: (spaceId: string) => void;
  toggleExpandGroup: (key: BrainSpaceGroupKey) => void;
  expandGroup: (key: BrainSpaceGroupKey) => void;
  selectPage: (pageId: string | null) => void;
  resetBrain: () => void;
  setHasHydrated: (value: boolean) => void;
  moveSubtreeBetweenSpaces: (input: MoveSubtreeBetweenSpacesInput) => void;
  snapshotSubtree: (spaceId: string, rootPageId: string) => KnowledgePageMeta[];
  setPageServer: (pageId: string, server: KnowledgePageContent) => void;
  setPageTitle: (pageId: string, title: string) => void;
  setPageLoading: (pageId: string, loading: boolean) => void;
  setPageError: (pageId: string, error: string | null) => void;
  removePageEntry: (pageId: string) => void;
  startDraft: (pageId: string) => void;
  setDraftContent: (pageId: string, content: string) => void;
  revertDraft: (pageId: string) => void;
  setDraftSaveStatus: (pageId: string, status: BrainPageSaveStatus) => void;
  setDraftSaveError: (pageId: string, error: string) => void;
  markDraftSaved: (pageId: string, newServer: KnowledgePageContent) => void;
  rebaseDraftAfterSave: (
    pageId: string,
    newServer: KnowledgePageContent,
  ) => void;
  setDraftConflict: (pageId: string, conflict: BrainPageConflict) => void;
  clearDraftConflict: (pageId: string) => void;
  setDraftContentRejection: (
    pageId: string,
    issues: KnowledgeContentRejectedIssue[],
  ) => void;
  clearDraftContentRejection: (pageId: string) => void;
  incrementActiveMoveCount: () => void;
  decrementActiveMoveCount: () => void;
  setFavorites: (pages: KnowledgePageMeta[]) => void;
  addFavorite: (pageId: string, page?: KnowledgePageMeta) => void;
  removeFavorite: (pageId: string) => void;
  setLoadingFavorites: (loading: boolean) => void;
  setMobileBrainPageHeader: (header: BrainMobilePageHeader | null) => void;
  setSpacePages: (spaceId: string, pages: KnowledgePageMeta[]) => void;
  setSpacePagesLoading: (spaceId: string, loading: boolean) => void;
  setSpacePagesError: (spaceId: string, error: string) => void;
  appendSpaces: (
    ownerType: KnowledgeOwnerType,
    spaces: BrainSpace[],
    cursor: string | null,
    teamId?: string,
  ) => void;
  bumpSpacesEpoch: (ownerType: KnowledgeOwnerType) => number;
  claimLoadingMoreSpaces: (
    ownerType: KnowledgeOwnerType,
    epoch: number,
  ) => boolean;
  releaseLoadingMoreSpaces: (
    ownerType: KnowledgeOwnerType,
    epoch: number,
  ) => void;
  resetTeamSpacesCursor: () => void;
  ensureSpacePresent: (space: BrainSpace) => void;
  restoreSpace: (space: BrainSpace) => void;
  setPendingPageLocation: (loc: PendingPageLocation | null) => void;
  setPendingDeepLink: (id: string | null) => void;
  setViewedSpaceId: (id: string | null) => void;
  bumpSpacePageCount: (spaceId: string, delta: number) => void;
  setRecentPages(pages: KnowledgePageMeta[]): void;
  setLoadingRecentPages(loading: boolean): void;
  markRecentPagesStale(): void;
  clearRecentPagesStale(): void;
  setPageHistoryVersions(
    pageId: string,
    payload: {
      versions: KnowledgeVersionInfo[];
      cursor: string | null;
      hasMore: boolean;
      append: boolean;
      currentVersion: number;
    },
  ): void;
  setPageHistorySelectedVersion(pageId: string, version: number | null): void;
  cachePageHistoryDiff(
    pageId: string,
    version: number,
    diff: KnowledgePageVersionDiff,
  ): void;
  cachePageHistoryContent(
    pageId: string,
    version: number,
    content: KnowledgePageContentDetail,
  ): void;
  resetPageHistory(pageId: string): void;
  addRecentMoveTarget(target: RecentMoveTarget): void;
}

export type BrainStore = BrainState & BrainActions;

export type BrainSidebarSlice = Pick<
  BrainState,
  | 'spaces'
  | 'isLoadingSpaces'
  | 'isLoadingUserSpaces'
  | 'isLoadingTeamSpaces'
  | 'hasLoadedUserSpaces'
  | 'hasLoadedTeamSpaces'
  | 'loadError'
  | 'expandedPageIds'
  | 'expandedSpaceIds'
  | 'expandedGroupTypes'
  | 'selectedPageId'
  | 'activeMoveCount'
  | 'loadedSpaceIds'
  | 'loadingSpaceIds'
  | 'spacePageErrors'
  | 'pendingPageLocation'
  | 'pendingDeepLink'
  | 'viewedSpaceId'
  | 'removedPageIds'
  | 'removedSpaceIds'
  | 'userSpacesCursor'
  | 'teamSpacesCursor'
  | 'userSpacesEpoch'
  | 'teamSpacesEpoch'
  | 'userSpacesLoadingMoreEpoch'
  | 'teamSpacesLoadingMoreEpoch'
> &
  Pick<
    BrainActions,
    | 'setUserSpaces'
    | 'setTeamSpaces'
    | 'setLoadingUserSpaces'
    | 'setLoadingTeamSpaces'
    | 'setLoadError'
    | 'addSpace'
    | 'removeSpace'
    | 'updateSpace'
    | 'replaceSpace'
    | 'addPageToSpace'
    | 'removePageFromSpace'
    | 'replacePageInSpace'
    | 'updatePageInSpace'
    | 'toggleExpandPage'
    | 'expandPage'
    | 'collapsePage'
    | 'toggleExpandSpace'
    | 'toggleExpandGroup'
    | 'expandGroup'
    | 'selectPage'
    | 'resetBrain'
    | 'moveSubtreeBetweenSpaces'
    | 'snapshotSubtree'
    | 'incrementActiveMoveCount'
    | 'decrementActiveMoveCount'
    | 'setSpacePages'
    | 'setSpacePagesLoading'
    | 'setSpacePagesError'
    | 'appendSpaces'
    | 'bumpSpacesEpoch'
    | 'claimLoadingMoreSpaces'
    | 'releaseLoadingMoreSpaces'
    | 'resetTeamSpacesCursor'
    | 'ensureSpacePresent'
    | 'restoreSpace'
    | 'setPendingPageLocation'
    | 'setPendingDeepLink'
    | 'setViewedSpaceId'
    | 'bumpSpacePageCount'
  >;

export type BrainPagesSlice = Pick<
  BrainState,
  'pagesById' | 'mobileBrainPageHeader'
> &
  Pick<
    BrainActions,
    | 'setPageServer'
    | 'setPageTitle'
    | 'setPageLoading'
    | 'setPageError'
    | 'removePageEntry'
    | 'startDraft'
    | 'setDraftContent'
    | 'revertDraft'
    | 'setDraftSaveStatus'
    | 'setDraftSaveError'
    | 'markDraftSaved'
    | 'rebaseDraftAfterSave'
    | 'setDraftConflict'
    | 'clearDraftConflict'
    | 'setDraftContentRejection'
    | 'clearDraftContentRejection'
    | 'setMobileBrainPageHeader'
  >;

export type BrainFavoritesSlice = Pick<
  BrainState,
  'favoritePageIds' | 'favoritePages' | 'isLoadingFavorites'
> &
  Pick<
    BrainActions,
    'setFavorites' | 'addFavorite' | 'removeFavorite' | 'setLoadingFavorites'
  >;

export type BrainRecentPagesSlice = Pick<
  BrainState,
  'recentPages' | 'isLoadingRecentPages' | 'recentPagesStale'
> &
  Pick<
    BrainActions,
    | 'setRecentPages'
    | 'setLoadingRecentPages'
    | 'markRecentPagesStale'
    | 'clearRecentPagesStale'
  >;

export type BrainPageHistorySlice = Pick<BrainState, 'pageHistoryById'> &
  Pick<
    BrainActions,
    | 'setPageHistoryVersions'
    | 'setPageHistorySelectedVersion'
    | 'cachePageHistoryDiff'
    | 'cachePageHistoryContent'
    | 'resetPageHistory'
  >;

export type BrainRecentMoveTargetsSlice = Pick<
  BrainState,
  'recentMoveTargets'
> &
  Pick<BrainActions, 'addRecentMoveTarget'>;

export const INITIAL_BRAIN_STATE: BrainState = {
  spaces: null,
  isLoadingSpaces: false,
  isLoadingUserSpaces: false,
  isLoadingTeamSpaces: false,
  hasLoadedUserSpaces: false,
  hasLoadedTeamSpaces: false,
  loadError: null,
  expandedPageIds: new Set<string>(),
  expandedSpaceIds: new Set<string>(),
  expandedGroupTypes: new Set<BrainSpaceGroupKey>(),
  selectedPageId: null,
  pagesById: {},
  mobileBrainPageHeader: null,
  _hasHydrated: false,
  activeMoveCount: 0,
  favoritePageIds: [],
  favoritePages: [],
  isLoadingFavorites: false,
  loadedSpaceIds: new Set<string>(),
  loadingSpaceIds: new Set<string>(),
  spacePageErrors: {},
  pendingPageLocation: null,
  pendingDeepLink: null,
  viewedSpaceId: null,
  removedPageIds: new Set<string>(),
  removedSpaceIds: new Set<string>(),
  recentPages: [],
  isLoadingRecentPages: false,
  recentPagesStale: false,
  pageHistoryById: {},
  recentMoveTargets: [],
  userSpacesCursor: null,
  teamSpacesCursor: null,
  userSpacesEpoch: 0,
  teamSpacesEpoch: 0,
  userSpacesLoadingMoreEpoch: null,
  teamSpacesLoadingMoreEpoch: null,
};
